import type { TFunction } from 'i18next'

import type { components } from '#/api/schema'
import type { TStatusGroup } from '#lib/statusGroups'

/** Status union from the generated SubscriptionItem contract. */
export type TSubscriptionStatus = components['schemas']['SubscriptionItem']['status']

/**
 * Which subscriptions the end action applies to, mirroring the backend's
 * `SubscriptionStatus::canRequestEnding()`.
 */
export const ENDABLE_SUBSCRIPTION_STATUSES = ['active'] as const satisfies readonly TSubscriptionStatus[]

/**
 * Which subscriptions the super admin can close for good, mirroring the
 * backend's `SubscriptionStatus::canBeEnded()`. An admin set the end date;
 * this confirms it really happened.
 */
export const CLOSABLE_SUBSCRIPTION_STATUSES = ['ending'] as const satisfies readonly TSubscriptionStatus[]

export const isClosable = (subscriptionStatus: string | null): boolean =>
  subscriptionStatus !== null && (CLOSABLE_SUBSCRIPTION_STATUSES as readonly string[]).includes(subscriptionStatus)

export const isEndable = (subscriptionStatus: string | null): boolean =>
  subscriptionStatus !== null && (ENDABLE_SUBSCRIPTION_STATUSES as readonly string[]).includes(subscriptionStatus)

/**
 * Which subscriptions a card stop cancels, mirroring the backend's
 * `SubscriptionStatus::isCancelledWhenCardStops()`. `CANCELLABLE_STATUSES`
 * stays pinned to `canBeCancelled()`.
 */
export const CANCELLED_ON_CARD_STOP_SUBSCRIPTION_STATUSES = [
  'initial',
  'ready_for_request',
  'requested_at_provider',
] as const satisfies readonly TSubscriptionStatus[]

export const willBeCancelledOnCardStop = (status: string | null): boolean =>
  status !== null && (CANCELLED_ON_CARD_STOP_SUBSCRIPTION_STATUSES as readonly string[]).includes(status)

/** Statuses that free the type. `ending` stays open: it is still paid for. */
export const CLOSED_SUBSCRIPTION_STATUSES = [
  'cancelled',
  'ended',
  'expired',
] as const satisfies readonly TSubscriptionStatus[]

export const isOpenSubscriptionStatus = (status: string | null | undefined): boolean =>
  typeof status === 'string' && !(CLOSED_SUBSCRIPTION_STATUSES as readonly string[]).includes(status)

export interface ISubscriptionSummary {
  id: string
  type: string
  subscriptionName: string | null
  status: TSubscriptionStatus
  /**
   * The status string the API sent, including values outside `TSubscriptionStatus`.
   * The badge reads this; `status` stays the known union so steppers keep a defined step.
   */
  reportedStatus?: string
  startDate?: string | null
  endDate?: string | null
  /** Absent on callers that do not read the card contract, such as Profile and Users. */
  renewSubscription?: boolean
  /** Predecessor id when the renewal run created this term. Null for a first term. */
  renewedFromId?: string | null
}

export const SUBSCRIPTION_STATUS_VARIANTS: Record<TSubscriptionStatus, TStatusGroup> = {
  // In bestelling, Klaar om aan te vragen and Aangevraagd: all three are being handled.
  initial: 'info',
  ready_for_request: 'info',
  requested_at_provider: 'info',
  active: 'success',
  ending: 'warning',
  ended: 'neutral',
  expired: 'neutral',
  cancelled: 'neutral',
}

/**
 * The one i18n key per subscription status. `initial` and `ending` use an
 * i18next context (`subscription`) because plain `Ordered` is the card label
 * (Dutch 'Besteld') and plain `Ending` is the proposition label (Dutch
 * 'Aflopend'). The separator is `_`, so the catalog keys are
 * `Ordered_subscription` and `Ending_subscription`.
 */
export const SUBSCRIPTION_STATUS_LABEL_KEYS: Record<TSubscriptionStatus, string> = {
  initial: 'Ordered_subscription',
  ready_for_request: 'Ready to request',
  requested_at_provider: 'Requested',
  active: 'Active',
  ending: 'Ending_subscription',
  ended: 'Ended',
  expired: 'Expired',
  cancelled: 'Cancelled',
}

export const subscriptionStatusLabelKey = (status: TSubscriptionStatus): string =>
  SUBSCRIPTION_STATUS_LABEL_KEYS[status]

// Object.keys widens to string[]. SUBSCRIPTION_STATUS_VARIANTS is what the compiler checks.
export const SUBSCRIPTION_STATUSES: readonly TSubscriptionStatus[] = Object.keys(
  SUBSCRIPTION_STATUS_VARIANTS,
) as TSubscriptionStatus[]

const isKnownStatus = (status: string): status is TSubscriptionStatus =>
  (SUBSCRIPTION_STATUSES as readonly string[]).includes(status)

export const toSubscriptionSummary = (item: {
  id: string
  subscriptionType?: string | null
  subscriptionName?: string | null
  status: string
  startDate?: string | null
  plannedStartDate?: string | null
  endDate?: string | null
  renewSubscription?: boolean
  renewedFromId?: string | null
}): ISubscriptionSummary => ({
  id: item.id,
  type: item.subscriptionType ?? '',
  subscriptionName: item.subscriptionName ?? null,
  status: isKnownStatus(item.status) ? item.status : 'initial',
  reportedStatus: item.status,
  startDate: item.startDate ?? item.plannedStartDate ?? null,
  endDate: item.endDate ?? null,
  renewSubscription: item.renewSubscription,
  renewedFromId: item.renewedFromId ?? null,
})

/** Open subscriptions first, keeping newest-first order within each group. */
export const openFirst = <T extends { status: string }>(subscriptions: readonly T[]): T[] => [
  ...subscriptions.filter((item) => isOpenSubscriptionStatus(item.status)),
  ...subscriptions.filter((item) => !isOpenSubscriptionStatus(item.status)),
]

/** Backend is the source of truth for subscription types — this is kept as a plain string alias */
export type TSubscriptionType = string

export interface ISubscription {
  id: string
  type: TSubscriptionType
  status: TSubscriptionStatus
  startDate: string | null
  endDate: string | null
  createdAt: string | null
}

export interface ISubscriptionStats {
  active: number
  requested: number
  ending: number
  ended: number
}

/**
 * Returns the translated label for a subscription status.
 * Uses static t() calls so i18next-parser can detect and extract the keys.
 */
export function getSubscriptionStatusLabel(status: TSubscriptionStatus, t: TFunction): string {
  // Static calls so i18next-parser sees the keys. The context ones resolve to
  // the same catalog entries as SUBSCRIPTION_STATUS_LABEL_KEYS.
  switch (status) {
    case 'initial':
      return t('Ordered', { context: 'subscription' })
    case 'ready_for_request':
      return t('Ready to request')
    case 'requested_at_provider':
      return t('Requested')
    case 'active':
      return t('Active')
    case 'ending':
      return t('Ending', { context: 'subscription' })
    case 'ended':
      return t('Ended')
    case 'expired':
      return t('Expired')
    case 'cancelled':
      return t('Cancelled')
    // Not the enforcement point: SUBSCRIPTION_STATUS_VARIANTS is the Record the
    // compiler checks against the contract. An unlisted status falls through to its code.
    default:
      return status
  }
}

/**
 * Returns the translated label for a subscription type.
 * Uses static t() calls so i18next-parser can detect and extract the keys.
 * Combines all known types from both subscriptions and contract settings.
 */
export function getSubscriptionTypeLabel(type: TSubscriptionType, t: TFunction): string {
  switch (type) {
    case 'ns_dal_voordeel':
      return t('NS Dal Voordeel')
    case 'ns_altijd_voordeel':
      return t('NS Altijd Voordeel')
    case 'ns_dal_vrij':
      return t('NS Dal Vrij')
    case 'ns_flex':
      return t('NS Flex')
    case 'ns_weekend_vrij':
      return t('NS Weekend Vrij')
    case 'ns_altijd_vrij':
      return t('NS Altijd Vrij')
    case 'ns_traject_vrij':
      return t('NS Traject Vrij')
    case 'ns_business_card':
      return t('NS Business Card')
    case 'ns_business_card_plus':
      return t('NS Business Card Plus')
    case 'ret_year_full':
      return t('RET Year Full')
    case 'ret_year_reduced':
      return t('RET Year Reduced')
    case 'ret_region_year_full':
      return t('RET Region Year Full')
    case 'ret_region_year_reduced':
      return t('RET Region Year Reduced')
    case 'gvb_randstad_noord':
      return t('GVB Randstad Noord')
    case 'gvb_regio_year':
      return t('GVB Regio Year')
    case 'qbuzz_always_free':
      return t('Qbuzz Always Free')
    case 'qbuzz_always_free_zhn':
      return t('Qbuzz Always Free ZHN')
    case 'qbuzz_always_free_fr':
      return t('Qbuzz Always Free FR')
    case 'qbuzz_pzhn':
      return t('Qbuzz PZHN')
    case 'qbuzz_off_pace':
      return t('Qbuzz Off Pace')
    case 'qbuzz_off_pace_zhn':
      return t('Qbuzz Off Pace ZHN')
    case 'qbuzz_off_pace_fr':
      return t('Qbuzz Off Pace FR')
    case 'qbuzz_always_free_kids':
      return t('Qbuzz Always Free Kids')
    case 'transdev_pzhn':
      return t('Transdev PZHN')
    case 'transdev_randstad_noord':
      return t('Transdev Randstad Noord')
    case 'transdev_region_year':
      return t('Transdev Region Year')
    case 'connexxion_randstad_noord':
      return t('Connexxion Randstad Noord')
    case 'connexxion_region_year':
      return t('Connexxion Region Year')
    case 'ov_vrij':
      return t('OV Vrij')
    case 'ov_dal':
      return t('OV Dal')
    case 'ov_dal_vrij':
      return t('OV Dal Vrij')
    case 'ov_fiets':
      return t('OV-fiets')
    case 'bso':
      return t('BSO')
    case 'greenwheels':
      return t('Greenwheels')
    case 'custom_1':
      return t('Custom 1')
    case 'custom_2':
      return t('Custom 2')
    case 'custom_3':
      return t('Custom 3')
    default:
      return type
  }
}

export interface ISubscriptionZoneStarsRequirement {
  zone: boolean
  stars: boolean
}

const MIN_STARS = 1
const MAX_STARS = 6

/**
 * Only the stars can block a request. A zone is optional even on the types that
 * have one (DEV-2500): an admin who does not know it must still be able to
 * order, and the carrier is told whatever we have. Shared by the order wizard
 * and the card detail modal to keep the two from drifting apart.
 */
export function isStarsIncomplete(
  requirement: ISubscriptionZoneStarsRequirement | undefined,
  amountStars: number,
): boolean {
  if (!requirement) return false
  if (!requirement.stars) return false
  return amountStars < MIN_STARS || amountStars > MAX_STARS
}
